import React, { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import Layout from "../components/Layout";
import Breadcrumbs from "../components/Breadcrumbs";
import QuoteForm from "../components/QuoteForm";
import HeroBackground from "../components/HeroBackground";
import PreFooterCTA from "../components/PreFooterCTA";
import ServiceAreaMap from "../components/ServiceAreaMap";
import FaqList from "../components/FaqList";
import { SERVICE_AREAS, SERVICES, BUSINESS, SITE_URL, getFaqsByIds, getAreaContent } from "../data/business";

const NEARBY_AREA_COUNT = 6;
const TOP_SERVICE_COUNT = 6;

function buildAreaFaqs(area) {
  return [
    { id: `area-serve-${area.slug}`, q: `Do you really serve ${area.city}, ${area.state}?`,
      a: `Yes — ${area.city} is one of our core service areas. We work across ${area.city} and nearby Baltimore-metro neighborhoods every week.` },
    { id: `area-schedule-${area.slug}`, q: `How fast can you schedule service in ${area.city}?`,
      a: `For most ${area.city} addresses, we can typically schedule a visit within a day or two — and we're available 24/7 for drain & sewer emergencies, day or night.` },
    { id: `area-travel-${area.slug}`, q: `Is there a travel fee for ${area.city}?`,
      a: `No. Travel within our service area is included in your quoted price.` },
    ...getFaqsByIds(["gen-services", "gen-licensed-insured", "gen-free-estimates", "gen-emergencies"]),
  ];
}

function AreaHero({ area, breadcrumbs }) {
  return (
    // Match Home / Service hero: fills viewport above the fold (~100.5px chrome).
    <section className="relative isolate overflow-hidden text-white lg:min-h-[calc(100vh-100px)] lg:flex lg:items-center">
      <HeroBackground />
      <div className="abc-container w-full py-8 lg:py-12 grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <Breadcrumbs items={breadcrumbs} />
          <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[11px] sm:text-xs font-semibold uppercase tracking-widest">
            <MapPin className="w-3.5 h-3.5 text-[#E31D2B]" /> Local service area
          </div>
          <h1 className="font-display mt-3 text-[28px] leading-[1.1] sm:text-4xl lg:text-5xl font-extrabold sm:leading-tight" data-testid="area-h1">
            Drain &amp; Sewer Service in {area.city}, {area.state}
          </h1>
          {/* Short on mobile, full on desktop */}
          <p className="mt-3 sm:hidden text-[15px] text-white/85 leading-relaxed">{area.blurb}</p>
          <p className="mt-4 hidden sm:block text-white/85 max-w-xl text-base md:text-lg">{area.blurb} Drain Sights covers {area.city} homeowners with drain cleaning, sewer camera inspection, hydro-jetting, and sewer line work — with clear pricing and clean work.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent">
              <Phone className="w-5 h-5" /> Call {BUSINESS.phone}
            </a>
            <Link to="/contact" className="abc-btn abc-btn-ghost-light">Get a Free Quote <ArrowRight className="w-5 h-5" /></Link>
          </div>
          <div className="mt-4 flex items-center gap-2 text-[13px] sm:text-sm text-white/80">
            <ShieldCheck className="w-4 h-4 text-[#E31D2B]" /> Licensed &amp; insured · {BUSINESS.hours.weekdays}
          </div>
        </div>
        <div className="lg:col-span-5">
          <QuoteForm source={`area-${area.slug}`} />
        </div>
      </div>
    </section>
  );
}

function AreaContent({ area, topServices }) {
  const content = getAreaContent(area.slug);
  const intro = content?.intro || [
    `From older homes with original clay or cast-iron sewer lines to newer builds, we've seen the full range of drain and sewer issues across ${area.city}. That local experience means faster diagnosis and fewer surprises.`,
    `We treat ${area.city} like the neighbors we are. We answer the phone 24/7, show up when we say we will, and give you a clear price before any work begins.`,
  ];
  const localIssues = content?.localIssues || [];
  return (
    <div className="lg:col-span-8 space-y-10">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">Trusted drain &amp; sewer pros in {area.city}</h2>
        {intro.map((p, i) => (
          <p key={i} className="mt-3 text-slate-600 leading-relaxed" data-testid={`area-intro-${i}`}>{p}</p>
        ))}
      </div>

      {localIssues.length > 0 && (
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">Common drain &amp; sewer issues in {area.city}</h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-3">
            {localIssues.map((issue) => (
              <li key={issue} className="flex items-start gap-2 text-slate-700" data-testid="area-issue">
                <AlertCircle className="w-5 h-5 text-[#54616E] flex-shrink-0 mt-0.5" /> {issue}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">Drain &amp; sewer services we offer in {area.city}</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {topServices.map((s) => (
            <Link to={`/services/${s.slug}`} key={s.slug} className="abc-card p-4 flex items-center justify-between" data-testid={`area-service-${s.slug}`}>
              <span className="font-semibold text-[#1A1D21]">{s.name}</span>
              <ArrowRight className="w-4 h-4 text-[#54616E]" />
            </Link>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">{area.city} drain &amp; sewer FAQs</h2>
        <FaqList faqs={buildAreaFaqs(area)} />
      </div>
    </div>
  );
}

function AreaSidebar({ area, nearby }) {
  return (
    <aside className="lg:col-span-4 space-y-6">
      <div className="abc-card p-5">
        <h3 className="font-display font-bold text-[#1A1D21] flex items-center gap-2 mb-3"><MapPin className="w-4 h-4 text-[#54616E]" /> {area.city} on our service map</h3>
        <ServiceAreaMap highlightSlug={area.slug} compact />
      </div>
      <div className="abc-card p-6">
        <h3 className="font-display font-bold text-[#1A1D21] flex items-center gap-2"><MapPin className="w-4 h-4 text-[#54616E]" /> Nearby service areas</h3>
        <ul className="mt-3 space-y-1.5 text-sm">
          {nearby.map((n) => (
            <li key={n.slug}><Link to={`/service-areas/${n.slug}`} className="text-slate-700 hover:text-[#54616E]">{n.city}, {n.state} →</Link></li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl bg-gradient-to-br from-[#1A1D21] to-[#262A30] text-white p-6">
        <h3 className="font-display font-bold text-lg">Drain or sewer job in {area.city}?</h3>
        <p className="text-white/75 text-sm mt-1">Call any time — 24/7 emergency service — and we'll get you on the schedule.</p>
        <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent w-full justify-center mt-4">
          <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
        </a>
      </div>
    </aside>
  );
}

export default function ServiceAreaDetail() {
  const { slug } = useParams();
  const area = SERVICE_AREAS.find((a) => a.slug === slug);
  const breadcrumbs = useMemo(
    () => (area ? [{ label: "Service Areas", to: "/service-areas" }, { label: `${area.city}, ${area.state}` }] : []),
    [area]
  );
  if (!area) return <Navigate to="/service-areas" replace />;

  const nearby = SERVICE_AREAS.filter((a) => a.slug !== area.slug).slice(0, NEARBY_AREA_COUNT);
  const topServices = SERVICES.slice(0, TOP_SERVICE_COUNT);
  const content = getAreaContent(area.slug);
  const areaSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Drain & Sewer Service",
    name: `Drain & Sewer Service in ${area.city}, ${area.state}`,
    description: content?.metaDescription || area.blurb,
    provider: { "@type": "Plumber", "@id": `${SITE_URL}/#business`, name: BUSINESS.name },
    areaServed: { "@type": "City", name: `${area.city}, ${area.state}` },
    url: `${SITE_URL}/service-areas/${area.slug}`,
  };

  return (
    <Layout>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(areaSchema)}</script>
      </Helmet>
      <AreaHero area={area} breadcrumbs={breadcrumbs} />

      <section className="py-10 md:py-14 abc-bg-pale">
        <div className="abc-container grid lg:grid-cols-12 gap-10">
          <AreaContent area={area} topServices={topServices} />
          <AreaSidebar area={area} nearby={nearby} />
        </div>
      </section>

      <PreFooterCTA title={`Need drain or sewer help in ${area.city}?`} subtitle="Call or request a free quote. Local crews, fast response." />
    </Layout>
  );
}
